/* eslint-disable jsx-a11y/mouse-events-have-key-events */
import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Color } from 'lib/modelBuilder/entities/Color';
import { Cabinet } from 'lib/modelBuilder/entities/Cabinet';
import { createStructuredSelector } from 'reselect';
import { RootState } from 'containers/PanesPanel/types';
import { makeSelectLastHistoryCabinet } from 'containers/Configurator/selectors/history';
import { restoreCabinet } from 'containers/Configurator/actions/furniture';
import {
  makeSelectDrawersButtonItems,
  makeSelectDrawersHandleItems,
  makeSelectPaintColorHandles,
  makeSelectPaintTypeHandles,
} from 'containers/HandlesPanel/selectors';
import {
  selectPaintDrawerHandleAction,
  updateHandleDrawerAction,
} from 'containers/HandlesPanel/actions';
import {
  BROWN_COLOR,
  CREAM_COLOR,
  DoorHandleType,
  DrawerHandleType,
  GREEN_COLOR,
} from 'lib/modelBuilder/constants';
import ItemPicker from 'helpers/ItemPicker';
import ItemPickerList from 'components/ItemPickerList';
import { useTranslation } from 'react-i18next';
import { setColorsPanelAction } from 'containers/Configurator/actions/general';
import { PickerTitleCategory } from '../../../components/PickerTitleCategory';
import OthersHandlePicker from './OthersHandlePicker';
import useMedia from 'use-media';
import { ConfiguratorMode } from '../../ModeBar/reducer';
import { makeSelectConfiguratorMode } from '../../ModeBar/selectors';
import { makeSelectContext } from '../../Configurator/selectors/general';
import { FurnitureEditorContext } from '../../../lib/constraintsChecker/FurnitureEditor';

// import styled from 'styled-components/macro';

interface StateProps {
  itemsHandles: ItemPicker[];
  itemsButton: ItemPicker[];
  lastHistoryCabinet: Cabinet;
  mode: ConfiguratorMode;
  paintColor: Color | undefined;
  paintType: DrawerHandleType | DoorHandleType | undefined;
  context: FurnitureEditorContext;
}
const stateSelector = createStructuredSelector<RootState, StateProps>({
  itemsHandles: makeSelectDrawersHandleItems(),
  itemsButton: makeSelectDrawersButtonItems(),
  lastHistoryCabinet: makeSelectLastHistoryCabinet(),
  mode: makeSelectConfiguratorMode(),
  paintColor: makeSelectPaintColorHandles(),
  paintType: makeSelectPaintTypeHandles(),
  context: makeSelectContext(),
});

const DrawersHandlesColorPicker = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const {
    itemsHandles,
    itemsButton,
    lastHistoryCabinet,
    mode,
    paintColor,
    paintType,
    context,
  } = useSelector(stateSelector);
  const [pickerSelected, setPicker] = useState(DrawerHandleType.Knop);

  const colorSelected = (
    type: DrawerHandleType,
    color: Color,
    preview: boolean,
  ) => {
    dispatch(updateHandleDrawerAction(type, color, preview));
  };

  const onClickButton = (item: ItemPicker) => {
    if (mode === ConfiguratorMode.handles) {
      dispatch(
        selectPaintDrawerHandleAction(
          DrawerHandleType.Button,
          item.color as Color,
        ),
      );
    } else {
      colorSelected(DrawerHandleType.Button, item.color as Color, false);
    }
  };

  const onMouseOverButton = (item: ItemPicker) => {
    if (mode === ConfiguratorMode.selector)
      colorSelected(DrawerHandleType.Button, item.color as Color, true);
  };

  const onClickKnop = (item: ItemPicker) => {
    if (mode === ConfiguratorMode.handles) {
      dispatch(
        selectPaintDrawerHandleAction(
          DrawerHandleType.Knop,
          item.color as Color,
        ),
      );
    } else {
      colorSelected(DrawerHandleType.Knop, item.color as Color, false);
    }
  };

  const onMouseOverHandle = (item: ItemPicker) => {
    if (mode === ConfiguratorMode.selector)
      colorSelected(DrawerHandleType.Knop, item.color as Color, true);
  };

  const onLeave = () => {
    // Restore the last history element
    dispatch(restoreCabinet(lastHistoryCabinet));
  };

  const isMobile = useMedia({ maxWidth: 1023 });

  let knopColorSelected: Color | undefined;
  let buttonColorSelected: Color | undefined;
  if (mode === ConfiguratorMode.handles) {
    if (paintType === DrawerHandleType.Knop) knopColorSelected = paintColor;
    else if (paintType === DrawerHandleType.Button)
      buttonColorSelected = paintColor;
  }

  if (isMobile) {
    return (
      <>
        <div className="mx-2 px-2 px-1">
          <div className="flex bg-gray-300">
            <PickerTitleCategory
              selected={pickerSelected === DrawerHandleType.Knop}
              onClick={() => {
                setPicker(DrawerHandleType.Knop);
              }}
            >
              {t('Handles')}
            </PickerTitleCategory>
            <PickerTitleCategory
              selected={pickerSelected === DrawerHandleType.Button}
              onClick={() => {
                setPicker(DrawerHandleType.Button);
              }}
            >
              {t('Buttons')}
            </PickerTitleCategory>
            <PickerTitleCategory
              selected={pickerSelected === DrawerHandleType.Slat}
              onClick={() => {
                setPicker(DrawerHandleType.Slat);
              }}
            >
              {t('Others')}
            </PickerTitleCategory>
          </div>
          {pickerSelected === DrawerHandleType.Knop && (
            <div onMouseLeave={onLeave}>
              <ItemPickerList
                items={itemsHandles.filter(
                  handle =>
                    ![CREAM_COLOR, BROWN_COLOR, GREEN_COLOR].includes(
                      handle.color?.reference ?? '',
                    ),
                )}
                onClick={onClickKnop}
                onDoubleClick={() => {
                  dispatch(setColorsPanelAction());
                }}
                onMouseOver={onMouseOverHandle}
              />
            </div>
          )}
          {pickerSelected === DrawerHandleType.Button && (
            <div onMouseLeave={onLeave}>
              <ItemPickerList
                items={itemsButton.filter(
                  handle =>
                    ![CREAM_COLOR].includes(handle.color?.reference ?? ''),
                )}
                onClick={onClickButton}
                onDoubleClick={() => {
                  dispatch(setColorsPanelAction());
                }}
                onMouseOver={onMouseOverButton}
              />
            </div>
          )}
          {pickerSelected === DrawerHandleType.Slat && <OthersHandlePicker />}
        </div>
      </>
    );
  } else {
    return (
      <>
        <div className="mx-2 px-2 px-1">
          <div className="flex bg-gray-300">
            <PickerTitleCategory
              selected={pickerSelected === DrawerHandleType.Knop}
              onClick={() => {
                setPicker(DrawerHandleType.Knop);
              }}
            >
              {t('Handles')}
            </PickerTitleCategory>
          </div>

          <div onMouseLeave={onLeave}>
            <ItemPickerList
              items={itemsHandles.filter(
                handle =>
                  ![CREAM_COLOR, BROWN_COLOR, GREEN_COLOR].includes(
                    handle.color?.reference ?? '',
                  ),
              )}
              onClick={onClickKnop}
              onDoubleClick={() => {
                if (mode === ConfiguratorMode.selector)
                  dispatch(setColorsPanelAction());
              }}
              onMouseOver={onMouseOverHandle}
              selectedColor={knopColorSelected}
            />
          </div>
        </div>

        <div className="mx-2 px-2 px-1">
          <div className="flex bg-gray-300">
            <PickerTitleCategory
              selected={pickerSelected === DrawerHandleType.Button}
              onClick={() => {
                setPicker(DrawerHandleType.Button);
              }}
            >
              {t('Buttons')}
            </PickerTitleCategory>
          </div>
          <div onMouseLeave={onLeave}>
            <ItemPickerList
              items={itemsButton.filter(
                handle =>
                  ![CREAM_COLOR].includes(handle.color?.reference ?? ''),
              )}
              onClick={onClickButton}
              onDoubleClick={() => {
                dispatch(setColorsPanelAction());
              }}
              onMouseOver={onMouseOverButton}
              selectedColor={buttonColorSelected}
            />
          </div>
        </div>
        <OthersHandlePicker />
      </>
    );
  }
};

export default DrawersHandlesColorPicker;
